import { useTheme } from "next-themes";
import { useAuth } from "../store/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { Shield, TrendingUp, Lock, CheckCircle, ArrowRight, Users, BarChart3, FileCheck, Zap, Globe } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useI18n } from "../hooks/useI18n";
import useWindowSize from "../hooks/useWindowSize";
import { fundBackLink } from "../components/customStyles";

export default function VerificationPage() {
	const screenSize = useWindowSize();
	const { theme } = useTheme();
	const { t } = useTranslation();
	const { isRTL } = useI18n();
	const navigate = useNavigate();

	const getThemeStyles = () => {
		const isDark = theme === "dark";

		return {
			container: {
				backgroundColor: isDark ? "var(--theme-dark-one)" : "var(--theme-light-one)",
				color: isDark ? "#f1f5f9" : "#1e293b",
				borderRadius: "20px",
				maxHeight: screenSize <= 575 ? "calc(100dvh - 127px)" : screenSize <= 991 && screenSize >= 576 ? "calc(100dvh - 176px)" : "calc(100dvh - 126px)",
				minHeight: screenSize <= 575 ? "calc(100dvh - 127px)" : screenSize <= 991 && screenSize >= 576 ? "calc(100dvh - 176px)" : "calc(100dvh - 126px)",
			},
			card: {
				maxWidth: "530px",
				color: isDark ? "#f1f5f9" : "#1e293b",
				backgroundColor: isDark ? "rgb(24, 30, 41)" : "rgb(245, 245, 245, .9)",
				border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
				boxShadow: isDark
					? "0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.1)"
					: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
			},
			separator: {
				backgroundColor: isDark ? "#475a76ff" : "rgb(180 183 188)",
			},
			button: {
				primary: {
					backgroundColor: isDark ? "rgb(47, 91, 163)" : "#2563eb",
					color: "#ddddddff",
					hover: isDark ? "rgba(53, 104, 186, 1)" : "#1d4ed8",
				},
				secondary: {
					backgroundColor: isDark ? "#374151" : "#f3f4f6",
					color: isDark ? "#f9fafb" : "#374151",
					border: `1px solid ${isDark ? "#4b5563" : "#d1d5db"}`,
					hover: isDark ? "#4b5563" : "#e5e7eb",
				},
			},
			accent: {
				primary: isDark ? "#3b82f6" : "#2563eb",
				success: isDark ? "#10b981" : "#059669",
				warning: isDark ? "#f59e0b" : "#d97706",
				subText: isDark ? "rgb(182, 182, 182)" : "rgba(34, 34, 34, 0.75)",
			},
		};
	};

	const styles = getThemeStyles();

  const handleVerification = () => {
    navigate("/auth/verify");
  };

	return (
		<div
			className={`add-custom-scroll${theme === "dark" ? "-dark" : ""} row w-100 mx-auto g-0 flex-nowrap justify-content-center align-items-center flex-column flex-xl-row position-relative`}
			style={{
				gap: screenSize >= 992 ? "60px" : screenSize <= 991 && screenSize >= 576 ? "30px" : "15px",
				overflowY: "auto",
				backgroundColor: theme === "dark" ? "var(--theme-dark-one)" : "var(--theme-light-one)",
				borderRadius: "20px",
				padding: screenSize >= 1200 ? "20px" : "60px 20px 40px 20px",
				maxHeight: screenSize <= 575 ? "calc(100dvh - 148px)" : screenSize <= 991 && screenSize >= 576 ? "calc(100dvh - 202px)" : "calc(100dvh - 128px)",
				minHeight: screenSize <= 575 ? "calc(100dvh - 148px)" : screenSize <= 991 && screenSize >= 576 ? "calc(100dvh - 202px)" : "calc(100dvh - 128px)",
				scrollbarWidth: screenSize >= 1200 ? "none" : screenSize <= 576 ? "none" : "",
			}}
		>
			<button
				className='d-inline-flex col-auto p-0 btn align-items-center position-absolute'
				type='button'
				onClick={() => navigate("/invest")}
				style={{
					...fundBackLink(theme),
					textDecoration: "none",
					left: "22px",
					top: "16px",
					fontSize: "18px",
					gap: "5px",
					zIndex: '100'
				}}
			>
				<svg
					xmlns='http://www.w3.org/2000/svg'
					width={24}
					height={24}
					viewBox='0 0 24 24'
					fill='none'
					stroke='currentColor'
					strokeWidth={2}
					strokeLinecap='round'
					strokeLinejoin='round'
					className='icon icon-tabler icons-tabler-outline icon-tabler-arrow-left'
				>
					<path stroke='none' d='M0 0h24v24H0z' fill='none' />
					<path d='M5 12l14 0' />
					<path d='M5 12l6 6' />
					<path d='M5 12l6 -6' />
				</svg>
				Back to Funds
			</button>

			<div
				className='w-100 p-4 p-md-5 rounded-3 position-relative overflow-hidden text-center'
				style={{
					maxWidth: "600px",
					color: theme === "dark" ? "#f1f5f9" : "#1e293b",
					backgroundColor: theme === "dark" ? "rgb(24, 30, 41)" : "#ffffff",
					border: `1px solid ${theme === "dark" ? "#334155" : "#e2e8f0"}`,
					boxShadow:
						theme === "dark"
							? "0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.1)"
							: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
				}}
			>
				<div className='position-absolute top-0 end-0 p-3'>
					<FileCheck size={24} style={{ color: styles.accent.primary, opacity: 0.6 }} />
				</div>

				<div className='mb-5'>
					<div
						className='rounded-circle p-3 mx-auto mb-4'
						style={{
							backgroundColor: styles.accent.primary + "20",
							width: "80px",
							height: "80px",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<Shield size={32} style={{ color: styles.accent.primary }} />
					</div>

					<h2 className='h3 fw-bold mb-3' style={{ color: styles.accent.primary }}>
						{t("app.guest.verify.title")}
					</h2>
					<p className='mb-4' style={{ color: styles.accent.subText }}>
						{t("app.guest.verify.subtitle")}
					</p>
				</div>

				{/* <div className='mb-5'>
              <div className='row g-3'>
                <div className='col-12 col-sm-6'>
                  <div className='d-flex align-items-center'>
                    <div
                      className='rounded-circle me-3 d-flex align-items-center justify-content-center overflow-visible'
                      style={{ backgroundColor: styles.accent.primary + "20", height: "38px", width: "38px" }}
                    >
                      <TrendingUp size={20} style={{ color: styles.accent.success }} />
                    </div>
                    <div className='text-start'>
                      <h6 className='mb-0 fw-semibold'>Higher Limits</h6>
                      <small style={{ color: styles.accent.subText }}>Increase investment capacity</small>
                    </div>
                  </div>
                </div>

                <div className='col-12 col-sm-6'>
                  <div className='d-flex align-items-center'>
                    <div
                      className='rounded-circle me-3 d-flex align-items-center justify-content-center overflow-visible'
                      style={{ backgroundColor: styles.accent.primary + "20", height: "38px", width: "38px" }}
                    >
                      <BarChart3 size={20} style={{ color: styles.accent.primary }} />
                    </div>
                    <div className='text-start'>
                      <h6 className='mb-0 fw-semibold'>Premium Tools</h6>
                      <small style={{ color: styles.accent.subText }}>Advanced analytics access</small>
                    </div>
                  </div>
                </div>

                <div className='col-12 col-sm-6'>
                  <div className='d-flex align-items-center'>
                    <div
                      className='rounded-circle me-3 d-flex align-items-center justify-content-center overflow-visible'
                      style={{ backgroundColor: styles.accent.warning + "20", height: "38px", width: "38px" }}
                    >
                      <Globe size={20} style={{ color: styles.accent.warning }} />
                    </div>
                    <div className='text-start'>
                      <h6 className='mb-0 fw-semibold'>Global Access</h6>
                      <small style={{ color: styles.accent.subText }}>International markets</small>
                    </div>
                  </div>
                </div>

                <div className='col-12 col-sm-6'>
                  <div className='d-flex align-items-center'>
                    <div
                      className='rounded-circle me-3 d-flex align-items-center justify-content-center overflow-visible'
                      style={{ backgroundColor: styles.accent.success + "20", height: "38px", width: "38px" }}
                    >
                      <CheckCircle size={20} style={{ color: styles.accent.success }} />
                    </div>
                    <div className='text-start'>
                      <h6 className='mb-0 fw-semibold'>Priority Support</h6>
                      <small style={{ color: styles.accent.subText }}>Dedicated assistance</small>
                    </div>
                  </div>
                </div>
              </div>
            </div> */}

				<button
					onClick={handleVerification}
					className='btn btn-lg w-100 d-flex align-items-center justify-content-center'
					style={{
						backgroundColor: styles.button.primary.backgroundColor,
						color: styles.button.primary.color,
						border: "none",
						fontWeight: "500",
						borderRadius: "12px",
						fontSize: "18px",
						padding: "12px 24px",
						transition: "all 0.3s ease",
						maxWidth: "300px",
						margin: "0 auto",
					}}
					onMouseEnter={(e) => {
						e.target.style.backgroundColor = styles.button.primary.hover;
						e.target.style.transform = "translateY(-2px)";
					}}
					onMouseLeave={(e) => {
						e.target.style.backgroundColor = styles.button.primary.backgroundColor;
						e.target.style.transform = "translateY(0)";
					}}
				>
					{t("app.guest.verify.button")}
					<ArrowRight size={20} className='ms-2' />
				</button>

				<div className='mt-3'>
					<small style={{ color: styles.accent.subText, opacity: ".8" }}>{t("app.guest.verify.caption")}</small>
				</div>
			</div>
		</div>
	)
}